import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  getCryptoHoldings, createCryptoHolding, updateCryptoHolding, deleteCryptoHolding,
  cryptoCoinSearch, connectCoinDCX, syncCoinDCX, listCryptoExchanges, disconnectCryptoExchange,
} from "../lib/api";
import {
  Bitcoin, Plus, RefreshCw, Trash2, Edit2, X, Check, Loader2, Link as LinkIcon,
  TrendingUp, TrendingDown, IndianRupee, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";

function fmtINR(v) {
  if (v == null || isNaN(v)) return "--";
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
function fmtNum(v, d = 2) {
  if (v == null || isNaN(v)) return "--";
  return v.toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });
}

export default function CryptoPage() {
  const [data, setData] = useState({ holdings: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exchanges, setExchanges] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sortKey, setSortKey] = useState("current_value");
  const [sortDir, setSortDir] = useState("desc");

  const load = useCallback(async () => {
    setError(null);
    const [h, e] = await Promise.allSettled([getCryptoHoldings(), listCryptoExchanges()]);
    if (h.status === "fulfilled") setData(h.value.data);
    else setError("Could not load crypto holdings");
    if (e.status === "fulfilled") setExchanges(e.value.data?.exchanges || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data: r } = await syncCoinDCX();
      toast.success(`Synced ${r.synced} coin${r.synced !== 1 ? "s" : ""} from CoinDCX`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    }
    setSyncing(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this crypto holding?")) return;
    try { await deleteCryptoHolding(id); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(["symbol", "account_label"].includes(key) ? "asc" : "desc"); }
  };

  const sortedHoldings = useMemo(() => {
    const isStr = ["symbol", "account_label"].includes(sortKey);
    return [...(data.holdings || [])].sort((a, b) => {
      const av = a[sortKey] ?? (isStr ? "" : 0);
      const bv = b[sortKey] ?? (isStr ? "" : 0);
      if (isStr) return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      return sortDir === "asc" ? av - bv : bv - av;
    });
  }, [data.holdings, sortKey, sortDir]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="animate-spin text-[var(--brand)]" size={28} /></div>;

  const s = data.summary || {};
  const coindcxLinked = exchanges.some(e => e.exchange === "coindcx");

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-5" data-testid="crypto-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Bitcoin size={18} className="text-amber-400" />
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Crypto Portfolio</h1>
          </div>
          <p className="text-sm text-[var(--text-muted)] mt-0.5">{s.coin_count || 0} coins · live INR prices via CoinDCX + CoinGecko</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5 border-white/10 text-xs" data-testid="crypto-refresh-btn">
            <RefreshCw size={13} /> Refresh
          </Button>
          {coindcxLinked ? (
            <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} className="gap-1.5 border-white/10 text-xs" data-testid="crypto-sync-coindcx-btn">
              <RefreshCw size={13} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing..." : "Sync CoinDCX"}
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setConnectOpen(true)} className="gap-1.5 border-white/10 text-xs" data-testid="crypto-connect-coindcx-btn">
              <LinkIcon size={13} /> Connect CoinDCX
            </Button>
          )}
          <Button size="sm" onClick={() => setAddOpen(true)} className="gap-1.5 text-xs bg-[var(--brand)] hover:bg-[var(--brand)]/90" data-testid="crypto-add-btn">
            <Plus size={13} /> Add Coin
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPI label="Total Value" value={fmtINR(s.total_value)} icon={<IndianRupee size={14} />} testId="kpi-crypto-value" />
        <KPI label="Total Invested" value={fmtINR(s.total_invested)} icon={<IndianRupee size={14} />} testId="kpi-crypto-invested" />
        <KPI label="Total P&L" value={fmtINR(s.total_pnl)} sub={`${s.total_pnl_pct >= 0 ? "+" : ""}${s.total_pnl_pct || 0}%`}
          icon={s.total_pnl >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />} positive={s.total_pnl >= 0} testId="kpi-crypto-pnl" />
        <KPI label="Today's Move" value={fmtINR(s.day_change_inr)} sub={`${s.day_change_pct >= 0 ? "+" : ""}${s.day_change_pct || 0}%`}
          icon={s.day_change_inr >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />} positive={s.day_change_inr >= 0} testId="kpi-crypto-day" />
      </div>

      {/* Connected exchanges */}
      {exchanges.length > 0 && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-3" data-testid="crypto-exchanges">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <LinkIcon size={13} className="text-emerald-400" />
              <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">Connected Exchanges</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {exchanges.map(ex => (
                <div key={ex.exchange} className="flex items-center gap-2 bg-[var(--bg-base)] border border-white/10 rounded px-2.5 py-1 text-[11px]">
                  <span className="text-emerald-400">●</span>
                  <span className="text-white font-medium">{ex.label || ex.exchange}</span>
                  {ex.last_synced_at && <span className="text-[var(--text-muted)] text-[10px]">· synced {new Date(ex.last_synced_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
                  <button onClick={async () => {
                    if (!window.confirm(`Disconnect ${ex.label || ex.exchange}? This removes all synced holdings.`)) return;
                    await disconnectCryptoExchange(ex.exchange);
                    toast.success("Disconnected");
                    load();
                  }} className="text-[var(--text-muted)] hover:text-red-400 ml-1" title="Disconnect">
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {(data.holdings || []).length === 0 && !error && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-10 text-center" data-testid="crypto-empty">
          <Bitcoin size={36} className="text-[var(--text-muted)] mx-auto mb-3" />
          <h3 className="text-base font-semibold text-white mb-1">No crypto holdings yet</h3>
          <p className="text-sm text-[var(--text-muted)] mb-4">Add a coin manually or connect your CoinDCX account to auto-sync.</p>
          <div className="flex gap-2 justify-center">
            <Button size="sm" onClick={() => setAddOpen(true)} className="bg-[var(--brand)] hover:bg-[var(--brand)]/90 gap-1.5">
              <Plus size={13} /> Add Coin
            </Button>
            {!coindcxLinked && (
              <Button variant="outline" size="sm" onClick={() => setConnectOpen(true)} className="border-white/10 gap-1.5">
                <LinkIcon size={13} /> Connect CoinDCX
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Holdings table */}
      {(data.holdings || []).length > 0 && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden" data-testid="crypto-holdings-table">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <SortHead label="Coin" k="symbol" cur={sortKey} dir={sortDir} click={toggleSort} align="left" pl />
                  <SortHead label="Source" k="account_label" cur={sortKey} dir={sortDir} click={toggleSort} align="left" />
                  <SortHead label="Qty" k="quantity" cur={sortKey} dir={sortDir} click={toggleSort} />
                  <SortHead label="Avg (₹)" k="average_price_inr" cur={sortKey} dir={sortDir} click={toggleSort} />
                  <SortHead label="CMP (₹)" k="last_price_inr" cur={sortKey} dir={sortDir} click={toggleSort} />
                  <SortHead label="Day +/-" k="day_change_pct" cur={sortKey} dir={sortDir} click={toggleSort} />
                  <SortHead label="P&L" k="pnl" cur={sortKey} dir={sortDir} click={toggleSort} />
                  <SortHead label="Value" k="current_value" cur={sortKey} dir={sortDir} click={toggleSort} />
                  <TableHead className="pr-4 text-right text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedHoldings.map(h => (
                  <TableRow key={h.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`crypto-row-${h.symbol}`}>
                    <TableCell className="pl-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium text-white">{h.symbol}</span>
                        <span className="text-[10px] text-[var(--text-muted)] truncate max-w-[120px]">{h.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${h.source === "coindcx" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : "bg-indigo-500/10 text-indigo-400 border-indigo-500/20"}`}>
                        {h.source === "coindcx" ? "CoinDCX" : (h.account_label || "Manual")}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-[var(--text-secondary)]">{fmtNum(h.quantity, 4)}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[var(--text-secondary)]">
                      {h.average_price_inr > 0 ? fmtNum(h.average_price_inr, 0) : (
                        <span className="text-amber-400 text-[10px]" title="Auto-detected avg unreliable (mixed exchange routing). Click edit to set manually.">
                          set manually
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-white">{h.last_price_inr > 0 ? fmtNum(h.last_price_inr, 0) : "--"}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${h.day_change_pct >= 0 ? "text-[var(--positive)] bg-[var(--positive)]/10" : "text-[var(--negative)] bg-[var(--negative)]/10"}`}>
                        {h.day_change_pct >= 0 ? "+" : ""}{h.day_change_pct?.toFixed(2)}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      <span className={h.pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}>
                        {h.pnl !== 0 ? `${h.pnl >= 0 ? "+" : ""}${fmtINR(h.pnl)}` : "--"}
                      </span>
                      {h.pnl_pct !== 0 && (
                        <div className={`text-[10px] ${h.pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"} opacity-60`}>
                          {h.pnl_pct >= 0 ? "+" : ""}{h.pnl_pct?.toFixed(2)}%
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-white">{h.current_value > 0 ? fmtINR(h.current_value) : "--"}</TableCell>
                    <TableCell className="pr-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditing(h)} className="text-[var(--text-muted)] hover:text-white p-1" title="Edit" data-testid={`crypto-edit-${h.symbol}`}>
                          <Edit2 size={12} />
                        </button>
                        <button onClick={() => handleDelete(h.id)} className="text-[var(--text-muted)] hover:text-red-400 p-1" title="Delete" data-testid={`crypto-delete-${h.symbol}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Add Coin dialog */}
      {addOpen && <AddCoinDialog onClose={() => setAddOpen(false)} onSaved={load} />}
      {/* Edit dialog */}
      {editing && <EditCoinDialog holding={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {/* CoinDCX connect dialog */}
      {connectOpen && <ConnectCoinDCXDialog onClose={() => setConnectOpen(false)} onConnected={load} />}
    </div>
  );
}

function KPI({ label, value, sub, icon, positive, testId }) {
  const tone = positive === false ? "red" : positive === true ? "green" : "investment";
  return (
    <div data-tone={tone} className="semantic-card border rounded-md p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[9px] tracking-[0.1em] uppercase text-[var(--text-muted)] font-semibold">{label}</span>
        <span className={positive === false ? "text-[var(--negative)]" : positive === true ? "text-[var(--positive)]" : "text-[var(--brand)]"}>{icon}</span>
      </div>
      <div className="semantic-value font-mono text-xl font-medium tracking-tight">{value}</div>
      {sub && <span className={`font-mono text-[10px] ${positive === false ? "text-[var(--negative)]" : positive === true ? "text-[var(--positive)]" : "text-[var(--text-muted)]"}`}>{sub}</span>}
    </div>
  );
}

function SortHead({ label, k, cur, dir, click, pl, align = "right" }) {
  const active = cur === k;
  const cls = align === "left" ? "text-left" : "text-right";
  return (
    <TableHead className={`${cls} cursor-pointer select-none group ${pl ? "pl-4" : ""}`} onClick={() => click(k)} data-testid={`crypto-sort-${k}`}>
      <span className={`inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold ${active ? "text-[var(--brand)]" : "text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]"}`}>
        {label}{active ? (dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="opacity-40" />}
      </span>
    </TableHead>
  );
}

function AddCoinDialog({ onClose, onSaved }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState("");
  const [avg, setAvg] = useState("");
  const [label, setLabel] = useState("Manual");
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!query || picked) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await cryptoCoinSearch(query);
        setResults(data?.coins || []);
      } catch {}
      setSearching(false);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, picked]);

  const save = async () => {
    if (!picked) return toast.error("Pick a coin first");
    const q = parseFloat(qty);
    if (!q || q <= 0) return toast.error("Enter quantity");
    setSaving(true);
    try {
      await createCryptoHolding({
        coin_id: picked.id,
        symbol: picked.symbol,
        name: picked.name,
        quantity: q,
        average_price_inr: parseFloat(avg) || 0,
        account_label: label || "Manual",
      });
      toast.success(`Added ${picked.symbol}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-surface)] border-white/10 max-w-md" data-testid="crypto-add-dialog">
        <DialogHeader>
          <DialogTitle className="text-white" style={{ fontFamily: "Chivo" }}>Add Crypto Holding</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Coin</label>
            {picked ? (
              <div className="mt-1 flex items-center justify-between bg-[var(--bg-base)] border border-white/10 rounded p-2">
                <div className="flex items-center gap-2">
                  {picked.thumb && <img src={picked.thumb} alt="" className="w-5 h-5 rounded-full" />}
                  <span className="text-sm text-white font-medium">{picked.name}</span>
                  <span className="text-[10px] font-mono text-[var(--text-muted)]">{picked.symbol}</span>
                </div>
                <button onClick={() => { setPicked(null); setQuery(""); }} className="text-[var(--text-muted)] hover:text-white">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <Input
                  autoFocus
                  placeholder="Search bitcoin, ethereum..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="mt-1 bg-[var(--bg-base)] border-white/10"
                  data-testid="crypto-coin-search"
                />
                {searching && <div className="text-[10px] text-[var(--text-muted)] mt-1">Searching...</div>}
                {results.length > 0 && (
                  <div className="mt-1 bg-[var(--bg-base)] border border-white/10 rounded max-h-[200px] overflow-y-auto">
                    {results.map(c => (
                      <button key={c.id} onClick={() => { setPicked(c); setQuery(c.name); }}
                        className="w-full flex items-center gap-2 p-2 hover:bg-white/[0.04] text-left transition-colors"
                        data-testid={`crypto-coin-result-${c.symbol}`}>
                        {c.thumb && <img src={c.thumb} alt="" className="w-5 h-5 rounded-full" />}
                        <span className="text-sm text-white">{c.name}</span>
                        <span className="text-[10px] font-mono text-[var(--text-muted)]">{c.symbol}</span>
                        {c.market_cap_rank && <span className="text-[9px] text-[var(--text-muted)] ml-auto">#{c.market_cap_rank}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Quantity</label>
              <Input type="number" step="any" placeholder="0.05" value={qty} onChange={(e) => setQty(e.target.value)}
                className="mt-1 bg-[var(--bg-base)] border-white/10 font-mono" data-testid="crypto-qty-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Avg Buy Price (₹)</label>
              <Input type="number" step="any" placeholder="5000000" value={avg} onChange={(e) => setAvg(e.target.value)}
                className="mt-1 bg-[var(--bg-base)] border-white/10 font-mono" data-testid="crypto-avg-input" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Source / Wallet Label</label>
            <Input placeholder="Manual / Hardware / Binance..." value={label} onChange={(e) => setLabel(e.target.value)}
              className="mt-1 bg-[var(--bg-base)] border-white/10 text-sm" data-testid="crypto-label-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} className="border-white/10">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving || !picked || !qty} className="bg-[var(--brand)] hover:bg-[var(--brand)]/90 gap-1.5" data-testid="crypto-save-btn">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditCoinDialog({ holding, onClose, onSaved }) {
  const [qty, setQty] = useState(String(holding.quantity || ""));
  const [avg, setAvg] = useState(String(holding.average_price_inr || ""));
  const [label, setLabel] = useState(holding.account_label || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateCryptoHolding(holding.id, {
        quantity: parseFloat(qty) || 0,
        average_price_inr: parseFloat(avg) || 0,
        account_label: label,
      });
      toast.success("Updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-surface)] border-white/10 max-w-md" data-testid="crypto-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Edit {holding.symbol}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Quantity</label>
              <Input type="number" step="any" value={qty} onChange={(e) => setQty(e.target.value)}
                className="mt-1 bg-[var(--bg-base)] border-white/10 font-mono" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Avg Buy (₹)</label>
              <Input type="number" step="any" value={avg} onChange={(e) => setAvg(e.target.value)}
                className="mt-1 bg-[var(--bg-base)] border-white/10 font-mono" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Source / Wallet</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)}
              className="mt-1 bg-[var(--bg-base)] border-white/10 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} className="border-white/10">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-[var(--brand)] hover:bg-[var(--brand)]/90">
            {saving ? <Loader2 size={13} className="animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConnectCoinDCXDialog({ onClose, onConnected }) {
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [label, setLabel] = useState("CoinDCX");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!apiKey.trim() || !apiSecret.trim()) return setError("Both API key and secret are required");
    setSubmitting(true);
    setError("");
    try {
      const { data } = await connectCoinDCX({ api_key: apiKey.trim(), api_secret: apiSecret.trim(), label });
      toast.success(`Connected — ${data.synced} coin${data.synced !== 1 ? "s" : ""} synced`);
      onConnected();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Connection failed");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-surface)] border-white/10 max-w-md" data-testid="crypto-connect-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <LinkIcon size={16} className="text-amber-400" /> Connect CoinDCX
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-amber-500/5 border border-amber-500/20 rounded p-2.5 text-[11px] text-amber-300 leading-relaxed">
            Create a <strong>read-only</strong> API key on CoinDCX (Profile → API Dashboard). Disable trade & withdraw permissions for safety.
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">API Key</label>
            <Input autoFocus value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder="Paste API key"
              className="mt-1 bg-[var(--bg-base)] border-white/10 font-mono text-xs" data-testid="coindcx-key-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">API Secret</label>
            <Input type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)}
              placeholder="Paste API secret"
              className="mt-1 bg-[var(--bg-base)] border-white/10 font-mono text-xs" data-testid="coindcx-secret-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Label (optional)</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)}
              className="mt-1 bg-[var(--bg-base)] border-white/10 text-sm" />
          </div>
          {error && (
            <div className="flex items-start gap-2 bg-red-500/5 border border-red-500/20 rounded p-2 text-[11px] text-red-300">
              <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} className="border-white/10">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={submitting} className="bg-[var(--brand)] hover:bg-[var(--brand)]/90 gap-1.5" data-testid="coindcx-submit">
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
